import React from 'react'

import FormManager from "../../gd-form/common/form-manager"
import Form from "../../gd-form/form/form-ui5/form"
import FormRow from "../../gd-form/form/form-ui5/form-row"
import FormToggle from "../../gd-form/form-element/toggle-new/toggle"
import Input from "../../gd-form/form-element/input/input"
import FormError from "../../gd-form/common/form-error/form-error"
import FormTabsSelector from "./form-tabs-selector/form-tabs-selector"
import ShareLinkCopy from "./share-link-copy/share-link-copy"
import EmbedCode from "./embed-code/embed-code"
import DatetimeUtils from "../../gd/utils/datetime-utils"
import {SharedViewConfig} from "./@types"

// @ts-ignore
import Datepicker from "../../gd-form/form-element/datepicker/datepicker-new"

type Props = {
    data            : {
        publicKey   : string
        title       : string
        config      : {
            openTasks       : boolean
            expirationDate  : string
        }
    }

    showOpenTasks   : boolean
    showOpenEvents  : boolean
    showOpenDocs    : boolean

    onCreate        : func
    onEdit          : func
    onRemove        : func
}

const debug = false
// const debug = "ShareViewForm"

export default class ShareViewForm extends React.Component<Props> {

    private data            : any
    private editDebounced   : any
    private form            : FormManager

    constructor(props: Props) {
        super(props)
        this.data = props.data

        this.editDebounced = _.debounce(this.onEdit, 300)

        const initialData = this.data ? this.data : {config:{}}
        const initFormData = {
            shared          : !!this.data,
            title           : _.get(initialData, 'title', ''),
            openTasks       : _.get(initialData, 'config.openTasks', false),
            openEvents      : _.get(initialData, 'config.openEvents', false),
            openDocs        : _.get(initialData, 'config.openDocs', false),
            expirationDate  : !_.isNil(initialData.config.expirationDate) ? DatetimeUtils.dateToMoment(initialData.config.expirationDate) : null
        }

        const formStructure = {
            fields: {
                shared          : { type: 'select',   isRequired: false,  onUserChange: this.onSharedChange},
                title           : { type: 'input',    isRequired: true,   onUserChange: this.onTitleEdit},
                openTasks       : { type: 'select',   isRequired: false,  onUserChange: this.onEdit},
                openEvents      : { type: 'select',   isRequired: false,  onUserChange: this.onEdit},
                openDocs        : { type: 'select',   isRequired: false,  onUserChange: this.onEdit},
                expirationDate  : { type: 'select',   isRequired: false,  onUserChange: this.onEdit},
            },
            isFormChanged: true,

            onSubmit: this.onSubmit
        }

        this.form = new FormManager(formStructure, initFormData)
    }

    componentWillUnmount() {
        if (this.editDebounced) this.editDebounced.cancel()
    }

    setData = (data: any) => {
        this.data = data

        this.form.setValue('title', _.get(data, 'title', ''),)
        this.form.setValue('openTasks', _.get(data, 'config.openTasks', false),)
        this.form.setValue('openEvents', _.get(data, 'config.openEvents', false),)
        this.form.setValue('openDocs', _.get(data, 'config.openDocs', false),)
        this.form.setValue('expirationDate', data && !_.isNil(data.config.expirationDate) ? DatetimeUtils.dateToMoment(data.config.expirationDate) : null)

        this.forceUpdate()
    }

    _collectConfig = (result: any) => {
        const res: SharedViewConfig = {};
        if (this.props.showOpenTasks && !_.isNil(result.openTasks)) res.openTasks = result.openTasks
        if (this.props.showOpenEvents && !_.isNil(result.openEvents)) res.openEvents = result.openEvents
        if (this.props.showOpenDocs && !_.isNil(result.openDocs)) res.openDocs = result.openDocs
        if (!_.isNil(result.expirationDate)) res.expirationDate = result.expirationDate.format("YYYY-MM-DD")
        return res
    }

    onSharedChange = () => {
        const result = this.form.getValues()
        if (!result.shared) this.props.onRemove(this.data.id)
        else                this.props.onCreate(this._collectConfig(result))
    }

    onSubmit    = (result: any) => {this.props.onEdit(this.data.id, result.title, this._collectConfig(result))}
    onTitleEdit = () => {this.editDebounced()}
    onEdit      = () => {this.form.onSubmitClick()}
    onLearnMore = () => {gd.openInNewWindow(gd.utils.helpUrl('apps.desktop'))}

    render() {
        const isShared = this.form.getValue('shared')

        return (
            <div className="ui5-plate modal-form">
                <Form form={this.form} size="size-30-70">
                    <div className="f-tbl">
                        <FormRow label="Public sharing">
                            <FormToggle size="20" form={this.form} name="shared" />

                            {!isShared &&
                                <div className="description ui5-txt-not-important-small">
                                    Public sharing <span className="highlight-description">allows anyone</span> with the link to access the view and its data.
                                    {/*<div className="more-link ui5-link-small" onClick={this.onLearnMore}>Learn more</div>*/}
                                </div>}
                        </FormRow>
                    </div>

                    {isShared &&
                        <React.Fragment>
                            <FormTabsSelector tabs={[
                                {label: "Link",         component: <ShareLinkCopy url={gdConfig.sharedViewPath + this.data.publicKey}/>},
                                {label: "Embed Code",   component: <EmbedCode url={gdConfig.sharedViewPath + this.data.publicKey}/>},
                            ]}/>

                            <div className="f-tbl">
                                <FormRow label="Title">
                                    <Input size="big" form={this.form} name="title"/>
                                    <FormError form={this.form} name="title" />
                                </FormRow>

                                {this.props.showOpenTasks &&
                                    <FormRow label="Allow Open Tasks">
                                        <FormToggle size="20" form={this.form} name="openTasks" />
                                    </FormRow>}

                                {this.props.showOpenEvents &&
                                    <FormRow label="Allow Open Events">
                                        <FormToggle size="20" form={this.form} name="openEvents" />
                                    </FormRow>}

                                {this.props.showOpenDocs &&
                                    <FormRow label="Allow Open Docs">
                                        <FormToggle size="20" form={this.form} name="openDocs" />
                                    </FormRow>}

                                <FormRow label="Expiration day">
                                    <Datepicker form            = {this.form}
                                                name            = "expirationDate"
                                                placeholderText = "Never"
                                                icon            = {<div className="icon ui5-arrow-toggle-9 open" style={{fontSize: '9px', top: '14px'}}/>}
                                                minDate         = {moment()}
                                                btnDelete       = {true}/>
                                </FormRow>
                            </div>
                        </React.Fragment>}
                </Form>
            </div>
        )
    }
}
